import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css, keyframes, ThemeProvider } from 'styled-components'

// Utils
import { AppStateCtx, useAppState } from '../context/AppContext'

// Styles
import { exitEase, fastDuration } from '../styles/effects'

// Components
import Loader from './Loader'
import Text from './Text'

interface IProps {
  isLoading: boolean
  isSigningIn: boolean
}

// Covers the app while a session starts, so the page builds up (company colours,
// language, tabs, the first data) out of sight and is revealed once, by fading this
// out. Straight after sign-in it repeats the sign-in page's own loading screen, so
// the hand-over doesn't show.
const AppLoadingScreen = ({ isLoading, isSigningIn }: IProps) => {
  const { t } = useTranslation()
  const state = useAppState()

  const [hasLeft, setHasLeft] = useState(!isLoading)
  // The state and copy it opened with: colours, culture rings and language turning
  // over underneath would otherwise flash on the loading screen itself
  const [opening, setOpening] = useState({ state, isSigningIn })
  if (isLoading && hasLeft) {
    setHasLeft(false)
    setOpening({ state, isSigningIn })
  }

  if (hasLeft) return null

  return (
    <AppStateCtx.Provider value={opening.state}>
      <ThemeProvider theme={opening.state.theme}>
        <Overlay
          $isLeaving={!isLoading}
          aria-live="polite"
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) setHasLeft(true)
          }}
        >
          <StatusScreen>
            <StatusLoader />
            {opening.isSigningIn && (
              <>
                <Text textStyle="displayM" textAlign="center">
                  {t('signIn.loadingTitle')}
                </Text>
                <Text marginTop={8} textAlign="center" color="textLight">
                  {t('signIn.loadingText')}
                </Text>
              </>
            )}
          </StatusScreen>
        </Overlay>
      </ThemeProvider>
    </AppStateCtx.Provider>
  )
}

export default AppLoadingScreen

// Shared with the sign-in page's status screens, which this one continues
export const StatusScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
`

export const StatusLoader = styled(Loader)`
  margin-bottom: 32px;
`

const fadeOut = keyframes`
  to {
    opacity: 0;
  }
`

// Above the navigation bar (1000), below dialogs (1100) and toasts (1500), so a
// "session expired" dialog still shows
const Overlay = styled.div<{ $isLeaving: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  background: ${({ theme }) => theme.colors.primaryLight};
  ${({ $isLeaving }) =>
    $isLeaving &&
    css`
      pointer-events: none;
      animation: ${fadeOut} ${fastDuration}ms ${exitEase} forwards;
    `}
`
